/**
 * Wires a header action button that may appear (and be re-created) after async render.
 *
 * Uses event delegation on `document` (capture phase) instead of binding to a specific
 * button node. React frequently replaces button DOM nodes during re-renders (e.g.
 * toggling disabled/loading state on Save), which would silently drop a listener
 * attached directly to the node. Delegation survives those re-renders and also fires
 * even if the app calls stopPropagation on the bubbling phase.
 *
 * Registration is idempotent per (onClicked, label) pair. Callers are often driven by a
 * MutationObserver that re-runs on every DOM change, so re-registering the same handler
 * reuses the existing listener instead of stacking duplicates.
 *
 * Call the returned function to remove the listener.
 */
const __ehvClickListeners =
  typeof window !== 'undefined'
    ? (window.__ehvClickListeners ||= new WeakMap())
    : new WeakMap()

const listenToButtonClick = (buttonText, onClicked) => {
  const label = (buttonText || '').trim()

  let byLabel = __ehvClickListeners.get(onClicked)
  if (!byLabel) {
    byLabel = new Map()
    __ehvClickListeners.set(onClicked, byLabel)
  }

  // Already wired for this (handler, label) pair — reuse it.
  if (byLabel.has(label)) {
    return byLabel.get(label).disconnect
  }

  const handler = (event) => {
    const button = event.target?.closest?.('button')
    if (!button) return
    if ((button.textContent || '').trim() !== label) return
    // Preserve the original semantics: only the first matching button in the
    // DOM triggers the callback. Delegation would otherwise fire for every
    // equally-labeled button, which the node-bound implementation never did.
    const firstMatch = Array.from(document.querySelectorAll('button')).find(
      (btn) => (btn.textContent || '').trim() === label
    )
    if (button !== firstMatch) return
    onClicked()
  }

  const disconnect = () => {
    document.removeEventListener('click', handler, true)
    const map = __ehvClickListeners.get(onClicked)
    if (map) {
      map.delete(label)
    }
  }

  document.addEventListener('click', handler, true)
  byLabel.set(label, { handler, disconnect })

  return disconnect
}
